import { fromPairs } from 'lodash'
import { htmlToText } from '@/utils/forms'

export const initialQuestions = [
  { id: 'company', text: 'What does your company do?', label: 'Company' },
  { id: 'problem', text: 'What problem do you solve?', label: 'Problem' },
  { id: 'solution', text: 'How do you solve this problem?', label: 'Solution' },
  {
    id: 'product',
    text: 'How does your product or service work? Please list its main features.',
    label: 'Product',
  },
  {
    id: 'key_innovation',
    text: 'What is the key innovation?',
    label: 'Key innovation',
  },
  {
    id: 'target_marked',
    text: 'What is your target market and its size?',
    label: 'Target market',
  },
  {
    id: 'market_moment',
    text: 'Why now is a good timing for your business?',
    label: 'Market moment',
  },
  {
    id: 'comptetitors',
    text: 'Who are your main competitors and why are you better?',
    label: 'Comptetitors',
  },
  {
    id: 'go_to_market_strategy',
    text: 'What is your marketing and sales strategy?',
    label: 'Go-to market strategy',
  },
  {
    id: 'business_model',
    text: 'What is your business and revenue model?',
    label: 'Business Model',
  },
  {
    id: 'traction',
    text: 'What traction have you achieved so far?',
    label: 'Traction',
  },
  {
    id: 'milestones',
    text: 'What are your next milestones?',
    label: 'Milestones',
  },
  {
    id: 'business_risks',
    text: 'What are the risks of the business?',
    label: 'Business risks',
  },
  {
    id: 'meet_cofounders',
    text: 'How did you meet your co-founders?',
    label: 'Co-founders',
  },
]

export const initialQuestionsMap = fromPairs(
  initialQuestions.map((q) => [q.id, q]),
)

const MAIN_SECTION_TITLE = 'Questions & Answers'
const initialSections = [
  {
    section: MAIN_SECTION_TITLE,
    questions: initialQuestions.map((q) => q.text),
  },
]

const initialFaq = initialSections.map((s) => ({
  section: s.section,
  items: s.questions.map((q) => ({
    question: q,
    answer: '',
  })),
}))

export default initialFaq

const initialSectionsMap = (function getInitialSectionsMap() {
  const mapping = {}
  initialSections.forEach(({ section, questions }) => {
    mapping[section] = new Set(questions)
  })
  return mapping
})()

export const toLegacyQuestionMap = {
  [initialQuestionsMap.product.text]: 'How does your product or service work?',
}

const legacyQuestions = new Set(['How does your product or service work?'])

export function isInitialQuestion({ sectionName, question }) {
  question = htmlToText(question)
  const section = initialSectionsMap[sectionName]
  return (
    (section && section.has(question)) ||
    (section && legacyQuestions.has(question))
  )
}

export function getQuestionTip({ sectionName, question }) {
  if (sectionName === MAIN_SECTION_TITLE) {
    return (
      {
        'What does your company do?':
          'Present briefly your vision or interesting facts about your product or service.',
        'What problem do you solve?':
          'What is the real problem your customers face? Describe the problem, show how it affects the end user. The more severe and painful the customers’ problem is, the higher will be the demand for your solution and the bigger investors’ interest in funding your project. Use a real customer story or a personal experience to illustrate the problem.',
        'How do you solve this problem?':
          'Demonstrate your product’s benefits and uniqueness and explain how it solves your customer’s problem. Show that with your product or service the problem will be a thing of the past.',
        'How does your product or service work?':
          'Explain how your product or service works. Show its main features and how it can change the customers’ life.',
        'How does your product or service work? Please list its main features.':
          'Explain how your product or service works. Show its main features and how it can change the customers’ life.',
        [initialQuestionsMap.product]:
          'Explain how your product or service works. Show its main features and how it can change the customers’ life.',
        'What is the development stage of your product and how do you plan to develop it further?':
          'Explain which features have already been implemented and which are still to be developed.',
        'What is the key innovation?':
          'Describe the technology and secret sauce behind your project.',
        'How do you protect your solution?':
          'What key intellectual property do you have (patents, copyrights, trademarks, domain names, licenses, technology transfer agreements etc.)? If you don’t protect the solution yet, present future plans of securing the product.',
        'What is your target market and its size?':
          'Show investors that you have a large group of prospective clients interested in buying your product. The bigger the market, the greater the potential payback on investment, so make sure that your company is pursuing a big market and will become a scalable business. Focus on determining who is your customer, how big is your market as a whole, what part of it do you plan to get and how fast is the market growing. Underline your market strategy - do you go global or concentrate on some specific region?',
        'Why now is a good timing for your business?':
          'Explain why your clients need this solution right now taking into account their habits and mindsets. Relate it to market trends, social changes, environmental threats etc.',
        'Why now is a good timing for your business':
          'Explain why your clients need this solution right now taking into account their habits and mindsets. Relate it to market trends, social changes, environmental threats etc.',
        'Why now is a good moment to enter the market?':
          'Explain why your clients need this solution right now taking into account their habits and mindsets. Relate it to market trends, social changes, environmental threats etc.',
        'Who are your main competitors and why are you better?':
          'Define your competitors providing few names and links to their websites. Highlight your competitive advantages - what makes your solution better? You can outcompete by having: a cutting-edge technology, a totally new approach to the problem, great team, additional functionalities, better user experience or considerably lower prices.',
        'What is your marketing and sales strategy?':
          'Describe how you are going to attract the customers and promote your solution - what are your key resources? Present distribution channels you plan to use, eg. direct sales force, partner network, sales through the internet and promotion activities. What marketing tools do you use? Advertisement, sales promotion, direct marketing, PR, personal sales? Outline the steps involved in acquiring customers. Describe the costs you have to bear, deal value and the needed time, key resources and strategic partnerships.',
        'What is your marketing and sales strategy':
          'Describe how you are going to attract the customers and promote your solution - what are your key resources? Present distribution channels you plan to use, eg. direct sales force, partner network, sales through the internet and promotion activities. What marketing tools do you use? Advertisement, sales promotion, direct marketing, PR, personal sales? Outline the steps involved in acquiring customers. Describe the costs you have to bear, deal value and the needed time, key resources and strategic partnerships.',
        'What is your business and revenue model?':
          'Business model is about how you are going to make money. It’s not only about revenue streams and pricing, but your overall business strategy. Describe your revenue model, how you are going to generate revenue from your products/ services and where your income streams will come from.',
        'What traction have you achieved so far?':
          'Show what milestones have you accomplished so far. You aim to prove that your business is well-balanced, viable and able to monetize acquired customers. Don’t forget to outline a press coverage, strategic partnerships, and customer success stories. If your company is at an early stage and pre-revenue with the product in development, draw at least a timeline with achieved milestones. It can be a product development, interviews with clients, letters of intent, number of beta testers, downloads or newsletter subscription, taking part in acceleration program or even winning a contest - whatever proves there is interest in your company or your product.',
        'What are your next milestones?':
          'Explain what you plan to achieve in the nearest future and lay out on a timeline the next milestones you are aiming.',
        'What are the risks of the business?':
          'Define main risks connected with running and developing your business. Take into account the whole business environment - all the external and internal threats to the project success.',
        'How did you meet your co-founders?':
          'Tell a short story about previous joint activities with your co-founders, especially if you’ve been working on some business together. If you are a solo founder, just state that.',
      }[question] || null
    )
  }
}
